import React from 'react';
import {useMutation} from '@tanstack/react-query';
import merge from 'lodash/merge';
import pick from 'lodash/pick';
import find from 'lodash/find';

import {useExperimentsStore, useTemporalStore} from 'apps/experiments/src/store';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {useExperimentsApi} from 'apps/experiments/src/api/experiments';
import {Experiment, ExperimentMetric, ExperimentUpdates} from 'iron/apps/experiments/src/types';
import {useContentSidenavContext} from 'common/widgets/content-sidenav/context';

import {useExperimentPageContext} from '../context';
import {UPDATEABLE_EXPERIMENT_FIELDS} from '../utils';

export function useUpdateExperiment() {
  const {projectId, workspaceId} = useExperimentPageContext();

  const experiment = useExperimentsStore((state) => state.experiment);
  const setExperiment = useExperimentsStore((state) => state.setExperiment);
  const setExperimentLoading = useExperimentsStore((state) => state.setExperimentLoading);
  const {pause: pauseHistory, resume: resumeHistory} = useTemporalStore((state) => state);
  const contentSidenavContext = useContentSidenavContext();

  const {updateExperiment} = useExperimentsApi({
    projectId,
    workspaceId,
  });

  const syncCurrentMetric = React.useCallback((newMetrics: ExperimentMetric[]) => {
    // need to fetch the current values from the store because these aren't always up to date
    // when the update is called
    const currentMetric = useExperimentsStore.getState().currentMetric;
    const setCurrentMetric = useExperimentsStore.getState().setCurrentMetric;
    if (!currentMetric) {
      return;
    }

    const metric = find(newMetrics, {id: currentMetric.id});
    if (metric) {
      setCurrentMetric({
        ...metric,
      });
    }
  }, []);

  const {mutate: updateMutate} = useMutation({
    mutationFn: updateExperiment,
    onSuccess: (data, variables) => {
      pauseHistory();
      setExperiment(data);
      resumeHistory();
      syncCurrentMetric(data.metrics);
      if (`name` in variables) {
        contentSidenavContext?.updateContentSidenavItem({
          content: data,
        });
      }
    },
    onError: (error) => {
      addToast({
        message: error.message,
        type: ToastType.ERROR,
        timeout: 6000,
      });
    },
    onSettled: () => {
      setExperimentLoading(false);
    },
  });

  type UpdateOptions = {
    persist?: boolean;
    optimistic?: boolean;
  };
  const update = React.useCallback(
    (updates: ExperimentUpdates, {persist = true, optimistic = true}: UpdateOptions = {}) => {
      let newState: Experiment;
      if (!persist || optimistic) {
        // eslint-disable-next-line prototype-pollution-security-rules/detect-merge-objects
        newState = merge({}, experiment, updates);
        // replace instead of merge to support reorders/deletions
        if (updates.metrics) {
          newState.metrics = updates.metrics;
          syncCurrentMetric(updates.metrics);
        }
        // replace global filters instead of merge
        if (updates.settings?.globalFilters) {
          newState.settings = {
            ...newState.settings,
            globalFilters: {
              filters: updates.settings.globalFilters.filters ?? [],
              variantsFilter: updates.settings.globalFilters.variantsFilter,
            },
          };
        }
        // replace exposures cache instead of merge
        if (updates.exposures_cache) {
          newState.exposures_cache = updates.exposures_cache;
        }
        // replace results summary instead of merge
        if (updates.results_cache?.summary) {
          newState.results_cache = {
            ...newState.results_cache,
            summary: updates.results_cache.summary,
          };
        }
        setExperiment(newState);
      }
      if (persist) {
        if (!optimistic) {
          setExperimentLoading(true);
        }
        updateMutate({
          id: experiment?.id ?? ``,
          ...pick(updates, UPDATEABLE_EXPERIMENT_FIELDS),
        });
      }
    },
    [experiment, updateMutate, setExperimentLoading, setExperiment, pauseHistory, resumeHistory, syncCurrentMetric],
  );

  return update;
}
